import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BriefcaseBusiness,
  MapPin,
  Users,
  Clock3,
  IndianRupee,
  CalendarDays,
  Building2,
  RefreshCw,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  UserRound,
  Mail,
  Award,
  TrendingUp,
  ChevronRight,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";
import { ArrowLeft } from "lucide-react";

/* =========================================================
   ALL APPLICATIONS - TRAINER PORTAL
   Displays every application returned by the backend
========================================================= */

export default function MyApplications() {
  const navigate = useNavigate();

  /* =======================================================
     STATE
  ======================================================= */

  const [applications, setApplications] = useState([]);
  const [trainer, setTrainer] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* =======================================================
     API
  ======================================================= */

  const TRAINERS_API =
    "/api/trainers/all";

  const APPLICATIONS_API =
    "/api/job-applications";

  /* =======================================================
     AUTH HEADERS
  ======================================================= */

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      ...(token
        ? {
          Authorization: `Bearer ${token}`,
        }
        : {}),
    };
  }, []);

  /* =======================================================
     LOGGED-IN USER
  ======================================================= */

  const getLoggedInUser = useCallback(() => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser);
    } catch (err) {
      console.error("USER PARSE ERROR:", err);
      return null;
    }
  }, []);

  /* =======================================================
     NORMALIZE API RESPONSE
  ======================================================= */

  const extractArray = (data) => {
    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.content)) {
      return data.content;
    }

    if (Array.isArray(data?.results)) {
      return data.results;
    }

    return [];
  };

  /* =======================================================
     GET LOGGED-IN TRAINER PROFILE

     Priority:
     1. trainerId from logged-in user
     2. trainer.id from logged-in user
     3. trainerId stored in localStorage
     4. Match logged-in email with trainer list
  ======================================================= */

  const getTrainerProfile = useCallback(async () => {
    const user = getLoggedInUser();

    if (!user) {
      throw new Error("Please login first.");
    }

    /* -------------------------------------------------------
       1. DIRECT TRAINER ID
    ------------------------------------------------------- */

    const directTrainerId =
      user?.trainerId ||
      user?.trainer?.id ||
      localStorage.getItem("trainerId");

    if (directTrainerId) {
      try {
        const trainerIdNumber = Number(directTrainerId);

        if (!Number.isNaN(trainerIdNumber)) {
          return {
            id: trainerIdNumber,
            name:
              user?.name ||
              user?.trainer?.name ||
              "Trainer",
            email:
              user?.email ||
              user?.trainer?.email ||
              "",
            phone:
              user?.phone ||
              user?.trainer?.phone ||
              "",
            experience:
              user?.experience ||
              user?.trainer?.experience ||
              "",
            skills:
              user?.skills ||
              user?.trainer?.skills ||
              "",
            state:
              user?.state ||
              user?.trainer?.state ||
              "",
            city:
              user?.city ||
              user?.trainer?.city ||
              "",
          };
        }
      } catch (err) {
        console.warn("DIRECT TRAINER ID ERROR:", err);
      }
    }

    /* -------------------------------------------------------
       2. EMAIL BASED MATCH
    ------------------------------------------------------- */

    const userEmail = user?.email
      ?.trim()
      .toLowerCase();

    if (!userEmail) {
      throw new Error(
        "Logged-in trainer email not found."
      );
    }

    const response = await fetch(
      TRAINERS_API,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );

    const responseText = await response.text();

    let data = null;

    try {
      data = responseText
        ? JSON.parse(responseText)
        : null;
    } catch {
      data = responseText;
    }

    if (!response.ok) {
      throw new Error(
        data?.message ||
        data?.error ||
        "Unable to fetch trainer profile."
      );
    }

    const trainers = extractArray(data);

    const matchedTrainer = trainers.find(
      (item) =>
        item?.email
          ?.trim()
          .toLowerCase() === userEmail
    );

    if (!matchedTrainer) {
      throw new Error(
        "Trainer profile not found for logged-in email."
      );
    }

    if (!matchedTrainer?.id) {
      throw new Error(
        "Trainer ID not found in trainer profile."
      );
    }

    /* Save trainer ID for future requests */

    localStorage.setItem(
      "trainerId",
      String(matchedTrainer.id)
    );

    return matchedTrainer;
  }, [
    getLoggedInUser,
    getAuthHeaders,
  ]);

  /* =======================================================
     FETCH ALL APPLICATIONS

     Uses GET /api/job-applications instead of
     GET /api/job-applications/trainer/{trainerId}.
     No trainer filter is applied on the frontend.
  ======================================================= */

  const fetchMyApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // 1. Identify the logged-in trainer.
      const trainerProfile = await getTrainerProfile();
      const trainerId =
        trainerProfile?.id ?? trainerProfile?.trainerId;

      if (!trainerId) {
        throw new Error(
          "Trainer ID could not be identified. Please check your trainer profile."
        );
      }

      setTrainer(trainerProfile);

      // 2. Fetch only this trainer's applications.
      const response = await fetch(
        `${APPLICATIONS_API}/trainer/${trainerId}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      const responseText = await response.text();

      let data = null;

      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        data = { message: responseText };
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
          data?.error ||
          `Unable to load applications. HTTP ${response.status}`
        );
      }

      const result = extractArray(data);

      console.log("TRAINER PROFILE:", trainerProfile);
      console.log("TRAINER APPLICATIONS:", result);

      setApplications(result);
    } catch (err) {
      console.error("MY APPLICATIONS ERROR:", err);
      setApplications([]);
      setError(err?.message || "Unable to load applications.");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getTrainerProfile]);

  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    fetchMyApplications();
  }, [fetchMyApplications]);

  /* =======================================================
     HELPERS
  ======================================================= */

  const safeString = (value) => {
    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }

    return String(value).trim();
  };

  const getTrainerName = () => {
    return (
      trainer?.name ||
      getLoggedInUser()?.name ||
      "Trainer"
    );
  };

  const getInitials = (name) => {
    const value = safeString(name);

    if (!value) {
      return "TR";
    }

    return value
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  const getSkills = () => {
    const skills =
      trainer?.skills ||
      "";

    if (Array.isArray(skills)) {
      return skills;
    }

    return String(skills)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 5);
  };

  /* =======================================================
     STATUS
  ======================================================= */

  const normalizeStatus = (status) => {
    return String(
      status || "PENDING"
    )
      .trim()
      .toUpperCase();
  };

  const getStatusClass = (status) => {
    const normalized =
      normalizeStatus(status);

    switch (normalized) {
      case "SELECTED":
        return "application-status selected";

      case "SHORTLISTED":
        return "application-status shortlisted";

      case "INTERVIEW":
      case "INTERVIEW_SCHEDULED":
        return "application-status interview";

      case "REJECTED":
        return "application-status rejected";

      case "WITHDRAWN":
        return "application-status withdrawn";

      default:
        return "application-status pending";
    }
  };

  const getStatusIcon = (status) => {
    const normalized =
      normalizeStatus(status);

    if (normalized === "SELECTED") {
      return (
        <CheckCircle2 size={14} />
      );
    }

    if (normalized === "REJECTED") {
      return (
        <XCircle size={14} />
      );
    }

    if (
      normalized === "INTERVIEW" ||
      normalized ===
      "INTERVIEW_SCHEDULED"
    ) {
      return (
        <CalendarDays size={14} />
      );
    }

    if (
      normalized === "SHORTLISTED"
    ) {
      return (
        <Award size={14} />
      );
    }

    return <Clock size={14} />;
  };

  const getReadableStatus = (status) => {
    const normalized =
      normalizeStatus(status);

    if (
      normalized ===
      "INTERVIEW_SCHEDULED"
    ) {
      return "INTERVIEW";
    }

    return normalized;
  };

  /* =======================================================
     DATE
  ======================================================= */

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    const parsed = new Date(date);

    if (
      Number.isNaN(
        parsed.getTime()
      )
    ) {
      return "-";
    }

    return parsed.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };
  const formatInterviewDateTime = (date, time) => {
    if (!date) return "Not scheduled";

    const dateTime = time
      ? `${date}T${time}`
      : `${date}T00:00:00`;

    const parsed = new Date(dateTime);

    if (Number.isNaN(parsed.getTime())) {
      return `${date}${time ? ` ${time}` : ""}`;
    }

    return parsed.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* =======================================================
     SALARY
  ======================================================= */

  const formatSalary = (
    min,
    max
  ) => {
    if (
      min == null &&
      max == null
    ) {
      return "Budget not specified";
    }

    if (
      min != null &&
      max != null
    ) {
      return `₹${Number(
        min
      ).toLocaleString(
        "en-IN"
      )} - ₹${Number(
        max
      ).toLocaleString(
        "en-IN"
      )}`;
    }

    if (min != null) {
      return `₹${Number(
        min
      ).toLocaleString(
        "en-IN"
      )}+`;
    }

    return `Up to ₹${Number(
      max
    ).toLocaleString(
      "en-IN"
    )}`;
  };

  /* =======================================================
     APPLICATION STATISTICS
  ======================================================= */

  const statistics = useMemo(() => {
    const stats = {
      total: applications.length,
      pending: 0,
      shortlisted: 0,
      interview: 0,
      selected: 0,
      rejected: 0,
    };

    applications.forEach(
      (application) => {
        const status =
          normalizeStatus(
            application?.applicationStatus
          );

        if (status === "PENDING") {
          stats.pending++;
        }

        if (
          status === "SHORTLISTED"
        ) {
          stats.shortlisted++;
        }

        if (
          status === "INTERVIEW" ||
          status ===
          "INTERVIEW_SCHEDULED"
        ) {
          stats.interview++;
        }

        if (
          status === "SELECTED"
        ) {
          stats.selected++;
        }

        if (
          status === "REJECTED"
        ) {
          stats.rejected++;
        }
      }
    );

    return stats;
  }, [applications]);

  /* =======================================================
     VIEW APPLICATION
  ======================================================= */

  const handleViewApplication = (
    application
  ) => {
    if (
      !application?.applicationId
    ) {
      return;
    }

    navigate(
      `/trainer/applications/${application.applicationId}`
    );
  };

  /* =======================================================
     BROWSE JOBS
  ======================================================= */

  const handleBrowseJobs = () => {
    navigate(
      "/trainer/available-jobs"
    );
  };

  /* =======================================================
     PROFILE
  ======================================================= */

  const handleProfile = () => {
    if (trainer?.id) {
      navigate(
        `/trainer/${trainer.id}`
      );
    }
  };

  /* =======================================================
     PAGE
  ======================================================= */

  return (
    <div className="trainer-applications-page">

      {/* =================================================
          TOP HEADER
      ================================================= */}

      <section className="applications-topbar">

        <div>
          <div className="page-breadcrumb">
            Trainer Portal
            <ChevronRight size={13} />
            My Applications
          </div>

          <div className="page-heading-row">

            <div className="page-heading-icon">
              <FileText size={22} />
            </div>
            <button
  type="button"
  className="applications-back-button"
  onClick={() => navigate("/trainer/dashboard")}
>
  <ArrowLeft size={18} />
  <span>Back to Dashboard</span>
</button>

            <div>
              <h1>
                My Applications
              </h1>

              <p>
                Track and manage jobs
                you have applied for.
              </p>
            </div>

          </div>
        </div>

        <button
          type="button"
          className="applications-refresh-btn"
          onClick={
            fetchMyApplications
          }
          disabled={loading}
        >
          <RefreshCw
            size={16}
            className={
              loading
                ? "spin"
                : ""
            }
          />

          Refresh
        </button>

      </section>

      {/* =================================================
          ERROR
      ================================================= */}

      {error && (
        <div className="applications-error">

          <div className="error-left">

            <AlertCircle size={18} />

            <div>
              <strong>
                Unable to load applications
              </strong>

              <span>
                {error}
              </span>
            </div>

          </div>

          <button
            type="button"
            onClick={
              fetchMyApplications
            }
          >
            Try Again
          </button>

        </div>
      )}

      {/* =================================================
          LOADING
      ================================================= */}

      {loading ? (
        <div className="applications-loading">

          <div className="loading-spinner">
            <RefreshCw
              size={28}
              className="spin"
            />
          </div>

          <h3>
            Loading your applications
          </h3>

          <p>
            Fetching your trainer
            profile and application history...
          </p>

        </div>
      ) : (
        <>
          {/* =============================================
              TRAINER PROFILE
          ============================================= */}

          {/* {trainer && (
            <section className="trainer-profile-banner">

              <div className="trainer-profile-main">

                <div className="trainer-avatar">
                  {getInitials(
                    getTrainerName()
                  )}
                </div>

                <div className="trainer-profile-info">

                  <div className="profile-name-row">

                    <h2>
                      {getTrainerName()}
                    </h2>

                    <span className="verified-badge">
                      <ShieldCheck
                        size={13}
                      />
                      Verified Trainer
                    </span>

                  </div>

                  <div className="profile-meta">

                    {trainer?.email && (
                      <span>
                        <Mail size={13} />
                        {trainer.email}
                      </span>
                    )}

                    {(trainer?.city ||
                      trainer?.state) && (
                      <span>
                        <MapPin
                          size={13}
                        />

                        {[
                          trainer.city,
                          trainer.state,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}

                    {trainer?.experience && (
                      <span>
                        <Award
                          size={13}
                        />
                        {trainer.experience}
                        {" "}
                        Experience
                      </span>
                    )}

                  </div>

                  {getSkills()
                    .length > 0 && (
                    <div className="profile-skills">

                      {getSkills().map(
                        (skill, index) => (
                          <span
                            key={`${skill}-${index}`}
                          >
                            {skill}
                          </span>
                        )
                      )}

                    </div>
                  )}

                </div>

              </div>

              <button
                type="button"
                className="view-profile-btn"
                onClick={
                  handleProfile
                }
              >
                View Profile
                <ArrowIcon />
              </button>

            </section>
          )} */}

          {/* =============================================
              STATISTICS
          ============================================= */}

          <section className="application-statistics">

            <div className="stat-card total">
              <div className="stat-icon">
                <BriefcaseBusiness
                  size={18}
                />
              </div>

              <div>
                <span>
                  Total Applications
                </span>
                <strong>
                  {statistics.total}
                </strong>
              </div>
            </div>

            <div className="stat-card pending">
              <div className="stat-icon">
                <Clock size={18} />
              </div>

              <div>
                <span>
                  Pending
                </span>
                <strong>
                  {statistics.pending}
                </strong>
              </div>
            </div>

            <div className="stat-card shortlisted">
              <div className="stat-icon">
                <Award size={18} />
              </div>

              <div>
                <span>
                  Shortlisted
                </span>
                <strong>
                  {statistics.shortlisted}
                </strong>
              </div>
            </div>

            <div className="stat-card interview">
              <div className="stat-icon">
                <CalendarDays
                  size={18}
                />
              </div>

              <div>
                <span>
                  Interview
                </span>
                <strong>
                  {statistics.interview}
                </strong>
              </div>
            </div>

            <div className="stat-card selected">
              <div className="stat-icon">
                <CheckCircle2
                  size={18}
                />
              </div>

              <div>
                <span>
                  Selected
                </span>
                <strong>
                  {statistics.selected}
                </strong>
              </div>
            </div>

          </section>

          {/* =============================================
              APPLICATIONS
          ============================================= */}

          {applications.length === 0 ? (

            <div className="applications-empty">

              <div className="empty-icon">
                <BriefcaseBusiness
                  size={38}
                />
              </div>

              <h3>
                No Applications Found
              </h3>

              <p>
                There are currently no job applications
                available in the system.
              </p>

              <button
                type="button"
                onClick={
                  handleBrowseJobs
                }
              >
                Browse Available Jobs
                <ChevronRight
                  size={16}
                />
              </button>

            </div>

          ) : (

            <section className="applications-section">

              <div className="applications-section-header">

                <div>
                  <h2>
                    All Job Applications
                  </h2>

                  
                </div>

                <div className="application-count">
                  {applications.length}
                  {" "}
                  {applications.length === 1
                    ? "Application"
                    : "Applications"}
                </div>

              </div>

              <div className="applications-grid">

                {applications.map(
                  (application, index) => {


                    const jobTitle =
                      application?.jobTitle ||
                      application?.job?.jobTitle ||
                      application?.title ||
                      `Job #${application?.jobId || "-"}`;

                    const location =
                      application?.jobLocation ||
                      application?.location ||
                      application?.job?.location ||
                      "-";

                    const companyName =
                      application?.companyName ||
                      application?.job?.companyName ||
                      "";

                    const employeeType =
                      application?.employeeType ||
                      application?.job?.employeeType ||
                      "-";

                    const experience =
                      application?.experienceRequired ||
                      application?.job?.experienceRequired ||
                      "-";

                    const salaryMin =
                      application?.salaryMin ??
                      application?.job?.salaryMin;

                    const salaryMax =
                      application?.salaryMax ??
                      application?.job?.salaryMax;

                    const appliedDate =
                      application?.appliedAt ||
                      application?.createdAt ||
                      application?.applicationDate;

                    const status = getReadableStatus(
                      application?.applicationStatus || application?.status
                    );

                    return (
                      <article
                        className="application-card"
                        key={
                          application?.applicationId ||
                          `${application?.jobId}-${index}`
                        }
                      >

                        {/* CARD TOP */}

                        <div className="application-card-top">

                          <div className="job-title-wrapper">

                            <div className="job-icon">
                              <BriefcaseBusiness
                                size={19}
                              />
                            </div>

                            <div>
                              <h3>
                                {jobTitle}
                              </h3>

                              <span>
                                Application ID:
                                {" "}
                                {application?.applicationId ||
                                  "-"}
                              </span>
                            </div>

                          </div>

                          <span
                            className={getStatusClass(
                              status
                            )}
                          >
                            {getStatusIcon(
                              status
                            )}

                            {status}
                          </span>

                        </div>

                        {/* STATUS LINE */}

                        <div className="application-status-line">

                          <div
                            className={
                              status ===
                                "SELECTED"
                                ? "status-step active"
                                : "status-step"
                            }
                          >
                            <span>
                              Applied
                            </span>
                          </div>

                          <div
                            className={
                              status ===
                                "SHORTLISTED" ||
                                status ===
                                "INTERVIEW" ||
                                status ===
                                "SELECTED"
                                ? "status-step active"
                                : "status-step"
                            }
                          >
                            <span>
                              Shortlisted
                            </span>
                          </div>

                          <div
                            className={
                              status ===
                                "INTERVIEW" ||
                                status ===
                                "SELECTED"
                                ? "status-step active"
                                : "status-step"
                            }
                          >
                            <span>
                              Interview
                            </span>
                          </div>

                          <div
                            className={
                              status ===
                                "SELECTED"
                                ? "status-step active"
                                : "status-step"
                            }
                          >
                            <span>
                              Selected
                            </span>
                          </div>

                        </div>

                        {/* JOB DETAILS */}

                        <div className="application-details">


                          <ApplicationDetail
                            icon={
                              <MapPin
                                size={14}
                              />
                            }
                            label="Location"
                            value={location}
                          />

                          <ApplicationDetail
                            icon={
                              <Building2
                                size={14}
                              />
                            }
                            label="Employment"
                            value={employeeType}
                          />

                          <ApplicationDetail
                            icon={
                              <Clock3
                                size={14}
                              />
                            }
                            label="Experience"
                            value={experience}
                          />

                          <ApplicationDetail
                            icon={
                              <IndianRupee
                                size={14}
                              />
                            }
                            label="Budget"
                            value={formatSalary(
                              salaryMin,
                              salaryMax
                            )}
                          />
                          <ApplicationDetail
                            icon={<BriefcaseBusiness size={14} />}
                            label="Job ID"
                            value={application?.jobId || "-"}
                          />

                          {companyName && (
                            <ApplicationDetail
                              icon={<Building2 size={14} />}
                              label="Company"
                              value={companyName}
                            />
                          )}

                          <ApplicationDetail
                            icon={
                              <CalendarDays
                                size={14}
                              />
                            }
                            label="Applied On"
                            value={formatDate(
                              appliedDate
                            )}
                          />

                          <ApplicationDetail
                            icon={
                              <Users
                                size={14}
                              />
                            }
                            label="Trainer ID"
                            value={
                              application?.trainerId ||
                              trainer?.id ||
                              "-"
                            }
                          />

                        </div>
                        {/* INTERVIEW INVITATION DETAILS */}

{(
  normalizeStatus(
    application?.applicationStatus || application?.status
  ) === "INTERVIEW" ||
  normalizeStatus(
    application?.applicationStatus || application?.status
  ) === "INTERVIEW_SCHEDULED"
) && (
  <div className="application-remarks">
    <strong
      style={{
        display: "flex",
        alignItems: "center",
        gap: "7px",
        color: "#3E0F8D",
        marginBottom: "10px",
      }}
    >
      <CalendarDays size={16} />
      Interview Invitation
    </strong>

    <div className="application-details">
      <ApplicationDetail
        icon={<CalendarDays size={14} />}
        label="Interview Date & Time"
        value={formatInterviewDateTime(
          application?.interviewDate,
          application?.interviewTime
        )}
      />

      <ApplicationDetail
        icon={<Users size={14} />}
        label="Interview Mode"
        value={application?.interviewMode || "Not specified"}
      />

      {application?.meetingLink && (
        <ApplicationDetail
          icon={<BriefcaseBusiness size={14} />}
          label="Meeting Link"
          value={
            <a
              href={application.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#3E0F8D",
                textDecoration: "underline",
                overflowWrap: "anywhere",
              }}
            >
              Join Interview
            </a>
          }
        />
      )}

      {application?.interviewLocation && (
        <ApplicationDetail
          icon={<MapPin size={14} />}
          label="Interview Venue"
          value={application.interviewLocation}
        />
      )}
    </div>

    {application?.interviewRemarks && (
      <div style={{ marginTop: "10px" }}>
        <strong>Message from partner</strong>
        <p style={{ marginTop: "5px" }}>
          {application.interviewRemarks}
        </p>
      </div>
    )}
  </div>
)}

                        {/* COVER LETTER */}

                        {application?.coverLetter && (
                          <div className="application-message">

                            <div className="message-label">
                              <FileText
                                size={13}
                              />
                              Cover Letter
                            </div>

                            <p>
                              {
                                application.coverLetter
                              }
                            </p>

                          </div>
                        )}

                        {/* REMARKS */}

                        {application?.remarks && (
                          <div className="application-remarks">

                            <strong>
                              Recruiter Remarks
                            </strong>

                            <p>
                              {
                                application.remarks
                              }
                            </p>

                          </div>
                        )}

                        {/* RESUME */}

                        {application?.resumeName && (
                          <div className="application-resume">

                            <FileText
                              size={15}
                            />

                            <span>
                              {
                                application.resumeName
                              }
                            </span>

                          </div>
                        )}

                        {/* FOOTER */}

                        <div className="application-card-footer">

                          <div className="current-status">

                            <span>
                              Current Status
                            </span>

                            <strong>
                              {status}
                            </strong>

                          </div>

                          <button
                            type="button"
                            className="view-application-btn"
                            onClick={() =>
                              handleViewApplication(
                                application
                              )
                            }
                            disabled={
                              !application?.applicationId
                            }
                          >
                            View Application

                            <ChevronRight
                              size={15}
                            />
                          </button>

                        </div>

                      </article>
                    );
                  }
                )}

              </div>

            </section>
          )}

          {/* =============================================
              BOTTOM SECURITY NOTE
          ============================================= */}

         
        </>
      )}

      <style>{MY_APPLICATIONS_CSS}</style>
    </div>
  );
}

/* =========================================================
   SMALL COMPONENTS
========================================================= */

function ApplicationDetail({
  icon,
  label,
  value,
}) {
  return (
    <div className="application-detail">

      <div className="application-detail-icon">
        {icon}
      </div>

      <div className="application-detail-content">

        <span>
          {label}
        </span>

        <strong>
          {value || "-"}
        </strong>

      </div>

    </div>
  );
}

function ArrowIcon() {
  return (
    <ChevronRight size={15} />
  );
}

/* =========================================================
   CSS
========================================================= */

const MY_APPLICATIONS_CSS = `

/* =========================================================
   CERTED BRAND VARIABLES
========================================================= */

.trainer-applications-page {
  --certed-primary: #3E0F8D;
  --certed-secondary: #9564DD;
  --certed-accent: #E4DA72;
  --certed-light: #EEEEEE;

  --certed-primary-soft: #f2ebff;
  --certed-secondary-soft: #f6f0ff;
  --certed-accent-soft: #fffbe2;

  --text-dark: #24183b;
  --text-main: #382c4e;
  --text-muted: #756b87;
  --text-light: #9b93a8;

  width: 100%;
  min-height: 100vh;
  padding: 30px;
  box-sizing: border-box;

  background:
    radial-gradient(
      circle at top right,
      rgba(149, 100, 221, .08),
      transparent 28%
    ),
    linear-gradient(
      180deg,
      #faf9fc 0%,
      #f5f3f8 100%
    );

  color: var(--text-main);

  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  font-size: 13px;
}

//my application page
.applications-back-button {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 20px;
  padding: 10px 15px;
  border: 1px solid #e4d9f5;
  border-radius: 10px;
  background: #ffffff;
  color: #3e0f8d;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.applications-back-button:hover {
  background: #f2eaff;
  border-color: #9564dd;
  transform: translateX(-2px);
}

.applications-back-button svg {
  flex-shrink: 0;
}
  .applications-back-button {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 20px;
  padding: 10px 15px;
  border: 1px solid #e4d9f5;
  border-radius: 10px;
  background: #ffffff;
  color: #3e0f8d;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.applications-back-button:hover {
  background: #f2eaff;
  border-color: #9564dd;
  transform: translateX(-2px);
}

.applications-back-button svg {
  flex-shrink: 0;
}


/* =========================================================
   TOP BAR
========================================================= */

.applications-topbar {
  max-width: 1500px;
  margin: 0 auto 24px;
margin-top:70px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  gap: 20px;
}

.page-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;

  margin-bottom: 10px;

  color: var(--text-light);

  font-size: 11px;
  font-weight: 600;

  letter-spacing: .02em;
}

.page-heading-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.page-heading-icon {
  width: 50px;
  height: 50px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 14px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  color: #ffffff;

  box-shadow:
    0 10px 25px
    rgba(62, 15, 141, .20);
}

.page-heading-row h1 {
  margin: 0;

  color: var(--text-dark);

  font-size: 27px;
  line-height: 1.2;
  font-weight: 800;

  letter-spacing: -.035em;
}

.page-heading-row p {
  margin: 6px 0 0;

  color: var(--text-muted);

  font-size: 13px;
  line-height: 1.5;
}

.applications-refresh-btn {
  height: 40px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding: 0 16px;

  border: 1px solid #ddd4ea;
  border-radius: 10px;

  background: #ffffff;

  color: var(--certed-primary);

  font-size: 12px;
  font-weight: 700;

  cursor: pointer;

  transition:
    all .22s ease;
}

.applications-refresh-btn:hover {
  border-color: var(--certed-secondary);

  background:
    linear-gradient(
      135deg,
      #ffffff,
      var(--certed-secondary-soft)
    );

  color: var(--certed-primary);

  transform: translateY(-1px);

  box-shadow:
    0 7px 18px
    rgba(62, 15, 141, .08);
}

.applications-refresh-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}


/* =========================================================
   ERROR
========================================================= */

.applications-error {
  max-width: 1500px;
  margin: 0 auto 20px;

  padding: 14px 16px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 16px;

  border: 1px solid #f1c7c7;
  border-radius: 11px;

  background: #fff7f7;

  color: #b42318;
}

.error-left {
  display: flex;
  align-items: center;
  gap: 11px;
}

.error-left div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.error-left strong {
  font-size: 12px;
  font-weight: 800;
}

.error-left span {
  color: #a54b45;
  font-size: 11px;
}

.applications-error button {
  border: 0;

  background: transparent;

  color: var(--certed-primary);

  font-size: 11px;
  font-weight: 800;

  cursor: pointer;
}


/* =========================================================
   LOADING
========================================================= */

.applications-loading {
  min-height: 520px;

  max-width: 1500px;

  margin: auto;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 68px;
  height: 68px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 19px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary-soft),
      var(--certed-secondary-soft)
    );

  color: var(--certed-primary);

  box-shadow:
    0 10px 25px
    rgba(62, 15, 141, .10);
}

.applications-loading h3 {
  margin: 19px 0 5px;

  color: var(--text-dark);

  font-size: 18px;
  font-weight: 800;
}

.applications-loading p {
  margin: 0;

  color: var(--text-muted);

  font-size: 12px;
}


/* =========================================================
   TRAINER PROFILE BANNER
========================================================= */

.trainer-profile-banner {
  max-width: 1500px;

  margin: 0 auto 20px;

  padding: 21px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 22px;

  border: 1px solid #e3dced;
  border-radius: 16px;

  background:
    linear-gradient(
      135deg,
      #ffffff 0%,
      #faf7ff 100%
    );

  box-shadow:
    0 9px 30px
    rgba(62, 15, 141, .07);

  position: relative;
  overflow: hidden;
}

.trainer-profile-banner::before {
  content: "";

  position: absolute;

  width: 180px;
  height: 180px;

  right: -80px;
  top: -100px;

  border-radius: 50%;

  background:
    rgba(149, 100, 221, .08);

  pointer-events: none;
}

.trainer-profile-main {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 16px;

  position: relative;
  z-index: 1;
}

.trainer-avatar {
  width: 66px;
  height: 66px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 17px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  color: #ffffff;

  font-size: 20px;
  font-weight: 800;

  box-shadow:
    0 11px 24px
    rgba(62, 15, 141, .22);
}

.trainer-profile-info {
  min-width: 0;
}

.profile-name-row {
  display: flex;
  align-items: center;

  flex-wrap: wrap;

  gap: 9px;
}

.profile-name-row h2 {
  margin: 0;

  color: var(--text-dark);

  font-size: 19px;
  font-weight: 800;
}

.verified-badge {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  padding: 5px 8px;

  border-radius: 20px;

  background:
    rgba(228, 218, 114, .28);

  color:
    #756800;

  font-size: 9px;
  font-weight: 800;

  text-transform: uppercase;

  letter-spacing: .04em;
}

.profile-meta {
  margin-top: 8px;

  display: flex;
  align-items: center;

  flex-wrap: wrap;

  gap: 14px;
}

.profile-meta span {
  display: inline-flex;
  align-items: center;

  gap: 6px;

  color: var(--text-muted);

  font-size: 11px;
}

.profile-meta svg {
  color: var(--certed-secondary);
}

.profile-skills {
  margin-top: 9px;

  display: flex;
  flex-wrap: wrap;

  gap: 6px;
}

.profile-skills span {
  padding: 5px 8px;

  border: 1px solid #ddd2ee;
  border-radius: 6px;

  background:
    var(--certed-secondary-soft);

  color:
    var(--certed-primary);

  font-size: 9px;
  font-weight: 700;
}

.view-profile-btn {
  height: 36px;

  flex-shrink: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  padding: 0 14px;

  border: 1px solid
    rgba(62, 15, 141, .18);

  border-radius: 9px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  color: #ffffff;

  font-size: 11px;
  font-weight: 800;

  cursor: pointer;

  transition: all .22s ease;

  position: relative;
  z-index: 1;
}

.view-profile-btn:hover {
  transform: translateY(-1px);

  box-shadow:
    0 8px 18px
    rgba(62, 15, 141, .20);
}


/* =========================================================
   STATISTICS
========================================================= */

.application-statistics {
  max-width: 1500px;

  margin: 0 auto 23px;

  display: grid;

  grid-template-columns:
    repeat(5, minmax(0, 1fr));

  gap: 13px;
}

.stat-card {
  min-width: 0;

  padding: 15px;

  display: flex;
  align-items: center;

  gap: 11px;

  border: 1px solid #e4dfeb;

  border-radius: 12px;

  background: #ffffff;

  box-shadow:
    0 5px 18px
    rgba(39, 23, 63, .045);

  transition:
    transform .22s ease,
    box-shadow .22s ease,
    border-color .22s ease;
}

.stat-card:hover {
  transform: translateY(-2px);

  border-color:
    rgba(149, 100, 221, .45);

  box-shadow:
    0 11px 26px
    rgba(62, 15, 141, .09);
}

.stat-icon {
  width: 37px;
  height: 37px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 10px;
}

.stat-card.total .stat-icon {
  background:
    var(--certed-primary-soft);

  color:
    var(--certed-primary);
}

.stat-card.pending .stat-icon {
  background:
    var(--certed-accent-soft);

  color:
    #827600;
}

.stat-card.shortlisted .stat-icon {
  background:
    var(--certed-secondary-soft);

  color:
    var(--certed-secondary);
}

.stat-card.interview .stat-icon {
  background:
    #eee6ff;

  color:
    #7043b8;
}

.stat-card.selected .stat-icon {
  background:
    #edf8f1;

  color:
    #19804b;
}

.stat-card span {
  display: block;

  color: var(--text-muted);

  font-size: 10px;
  font-weight: 600;
}

.stat-card strong {
  display: block;

  margin-top: 3px;

  color: var(--text-dark);

  font-size: 20px;
  font-weight: 800;
}


/* =========================================================
   SECTION HEADER
========================================================= */

.applications-section {
  max-width: 1500px;

  margin: 0 auto;
}

.applications-section-header {
  margin-bottom: 14px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  gap: 16px;
}

.applications-section-header h2 {
  margin: 0;

  color: var(--text-dark);

  font-size: 19px;
  font-weight: 800;

  letter-spacing: -.015em;
}

.applications-section-header p {
  margin: 5px 0 0;

  color: var(--text-muted);

  font-size: 11px;
}

.application-count {
  padding: 7px 10px;

  border: 1px solid #ded5e9;

  border-radius: 8px;

  background:
    var(--certed-secondary-soft);

  color:
    var(--certed-primary);

  font-size: 10px;
  font-weight: 800;
}


/* =========================================================
   APPLICATION GRID
========================================================= */

.applications-grid {
  display: grid;

  grid-template-columns:
    repeat(3, minmax(0, 1fr));

  gap: 17px;
}


/* =========================================================
   APPLICATION CARD
========================================================= */

.application-card {
  min-width: 0;

  display: flex;
  flex-direction: column;

  padding: 18px;

  border: 1px solid #e3deea;

  border-radius: 15px;

  background: #ffffff;

  box-shadow:
    0 6px 22px
    rgba(39, 23, 63, .045);

  transition:
    transform .22s ease,
    box-shadow .22s ease,
    border-color .22s ease;

  position: relative;
}

.application-card:hover {
  transform: translateY(-3px);

  border-color:
    rgba(149, 100, 221, .42);

  box-shadow:
    0 14px 34px
    rgba(62, 15, 141, .10);
}

.application-card-top {
  display: flex;
  align-items: flex-start;

  justify-content: space-between;

  gap: 12px;
}

.job-title-wrapper {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 10px;
}

.job-icon {
  width: 40px;
  height: 40px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 10px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary-soft),
      var(--certed-secondary-soft)
    );

  color:
    var(--certed-primary);
}

.job-title-wrapper h3 {
  margin: 0;

  color: var(--text-dark);

  font-size: 14px;
  line-height: 1.4;

  font-weight: 800;

  word-break: break-word;
}

.job-title-wrapper span {
  display: block;

  margin-top: 4px;

  color: var(--text-light);

  font-size: 9px;
}


/* =========================================================
   STATUS BADGES
========================================================= */

.application-status {
  min-height: 26px;

  flex-shrink: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 5px;

  padding: 0 9px;

  border: 1px solid;

  border-radius: 20px;

  font-size: 9px;
  font-weight: 800;

  letter-spacing: .01em;
}

.application-status.pending {
  background:
    var(--certed-accent-soft);

  border-color:
    #e5d77b;

  color:
    #756700;
}

.application-status.shortlisted {
  background:
    var(--certed-secondary-soft);

  border-color:
    #d1bbed;

  color:
    var(--certed-primary);
}

.application-status.interview {
  background:
    #eee6ff;

  border-color:
    #c9b2ed;

  color:
    #7042b6;
}

.application-status.selected {
  background:
    #edf8f1;

  border-color:
    #a8d8ba;

  color:
    #157746;
}

.application-status.rejected {
  background:
    #fff0f0;

  border-color:
    #efb5b5;

  color:
    #bd2e2e;
}

.application-status.withdrawn {
  background:
    var(--certed-light);

  border-color:
    #d4d0d8;

  color:
    #68606f;
}


/* =========================================================
   APPLICATION STATUS LINE
========================================================= */

.application-status-line {
  margin: 19px 0 16px;

  display: grid;

  grid-template-columns:
    repeat(4, 1fr);

  position: relative;
}

.application-status-line::before {
  content: "";

  position: absolute;

  left: 9px;
  right: 9px;

  top: 5px;

  height: 2px;

  background:
    #e7e2eb;
}

.status-step {
  position: relative;

  z-index: 1;

  text-align: center;
}

.status-step::before {
  content: "";

  width: 11px;
  height: 11px;

  margin: 0 auto 6px;

  display: block;

  border: 2px solid #d9d2e1;

  border-radius: 50%;

  background: #ffffff;

  box-sizing: border-box;
}

.status-step.active::before {
  border-color:
    var(--certed-primary);

  background:
    var(--certed-primary);

  box-shadow:
    0 0 0 3px
    var(--certed-primary-soft);
}

.status-step span {
  display: block;

  color: #aaa3b2;

  font-size: 8px;
  font-weight: 700;
}

.status-step.active span {
  color:
    var(--certed-primary);

  font-weight: 800;
}


/* =========================================================
   APPLICATION DETAILS
========================================================= */

.application-details {
  display: grid;

  grid-template-columns:
    repeat(2, minmax(0, 1fr));

  gap: 9px;

  padding: 12px;

  border-radius: 11px;

  background:
    #faf9fc;

  border:
    1px solid #eeeaf2;
}

.application-detail {
  min-width: 0;

  display: flex;
  align-items: flex-start;

  gap: 8px;
}

.application-detail-icon {
  width: 28px;
  height: 28px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid #e4deeb;

  border-radius: 7px;

  background: #ffffff;

  color:
    var(--certed-secondary);
}

.application-detail-content {
  min-width: 0;
}

.application-detail-content span {
  display: block;

  margin-bottom: 3px;

  color: var(--text-light);

  font-size: 8px;
  font-weight: 600;
}

.application-detail-content strong {
  display: block;

  color: var(--text-main);

  font-size: 10px;

  line-height: 1.4;

  word-break: break-word;
}


/* =========================================================
   COVER LETTER
========================================================= */

.application-message {
  margin-top: 13px;
}

.message-label {
  display: flex;
  align-items: center;

  gap: 6px;

  margin-bottom: 6px;

  color:
    var(--certed-primary);

  font-size: 10px;
  font-weight: 800;
}

.application-message p,
.application-remarks p {
  margin: 0;

  padding: 10px;

  border:
    1px solid #eeeaf2;

  border-radius: 8px;

  background:
    #faf9fc;

  color:
    var(--text-muted);

  font-size: 10px;

  line-height: 1.6;
}


/* =========================================================
   REMARKS
========================================================= */

.application-remarks {
  margin-top: 10px;
}

.application-remarks strong {
  display: block;

  margin-bottom: 6px;

  color:
    var(--text-main);

  font-size: 10px;
  font-weight: 800;
}


/* =========================================================
   RESUME
========================================================= */

.application-resume {
  margin-top: 11px;

  padding: 9px 10px;

  display: flex;
  align-items: center;

  gap: 7px;

  border:
    1px solid #ded1ef;

  border-radius: 8px;

  background:
    var(--certed-secondary-soft);

  color:
    var(--certed-primary);

  font-size: 9px;

  font-weight: 700;

  word-break: break-word;
}


/* =========================================================
   CARD FOOTER
========================================================= */

.application-card-footer {
  margin-top: auto;

  padding-top: 15px;

  display: flex;
  align-items: flex-end;

  justify-content: space-between;

  gap: 11px;

  border-top:
    1px solid #eeeaf2;
}

.current-status span {
  display: block;

  color: var(--text-light);

  font-size: 8px;
}

.current-status strong {
  display: block;

  margin-top: 3px;

  color: var(--certed-primary);

  font-size: 10px;

  font-weight: 800;
}

.view-application-btn {
  height: 33px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  padding: 0 11px;

  border: 1px solid
    rgba(62, 15, 141, .20);

  border-radius: 8px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  color: #ffffff;

  font-size: 10px;

  font-weight: 800;

  cursor: pointer;

  transition: all .22s ease;
}

.view-application-btn:hover {
  transform: translateY(-1px);

  box-shadow:
    0 7px 18px
    rgba(62, 15, 141, .18);
}

.view-application-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
}


/* =========================================================
   EMPTY STATE
========================================================= */

.applications-empty {
  max-width: 1500px;

  min-height: 430px;

  margin: 0 auto;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  text-align: center;

  padding: 30px;

  border:
    1px dashed #d9d0e4;

  border-radius: 16px;

  background: #ffffff;

  box-shadow:
    0 6px 22px
    rgba(39, 23, 63, .035);
}

.empty-icon {
  width: 82px;
  height: 82px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 22px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary-soft),
      var(--certed-secondary-soft)
    );

  color:
    var(--certed-primary);

  box-shadow:
    0 10px 24px
    rgba(62, 15, 141, .10);
}

.applications-empty h3 {
  margin: 17px 0 6px;

  color:
    var(--text-dark);

  font-size: 20px;

  font-weight: 800;
}

.applications-empty p {
  max-width: 470px;

  margin: 0 0 18px;

  color:
    var(--text-muted);

  font-size: 12px;

  line-height: 1.65;
}

.applications-empty button {
  height: 38px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  padding: 0 15px;

  border: 0;

  border-radius: 9px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  color: #ffffff;

  font-size: 11px;

  font-weight: 800;

  cursor: pointer;

  transition: all .22s ease;
}

.applications-empty button:hover {
  transform: translateY(-1px);

  box-shadow:
    0 9px 20px
    rgba(62, 15, 141, .20);
}


/* =========================================================
   SECURITY NOTE
========================================================= */

.applications-security-note {
  max-width: 1500px;

  margin: 21px auto 0;

  padding: 11px 13px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  border:
    1px solid #e3ddea;

  border-radius: 9px;

  background:
    rgba(255,255,255,.75);

  color:
    var(--text-muted);

  font-size: 9px;

  line-height: 1.5;

  text-align: center;
}

.applications-security-note svg {
  flex-shrink: 0;

  color:
    var(--certed-primary);
}


/* =========================================================
   ANIMATION
========================================================= */

.spin {
  animation:
    trainerApplicationsSpin
    1s linear infinite;
}

@keyframes trainerApplicationsSpin {

  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }

}


/* =========================================================
   RESPONSIVE - TABLET
========================================================= */

@media (max-width: 1250px) {

  .applications-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .application-statistics {
    grid-template-columns:
      repeat(3, minmax(0, 1fr));
  }

}


/* =========================================================
   RESPONSIVE - SMALL TABLET
========================================================= */

@media (max-width: 850px) {

  .trainer-applications-page {
    padding: 21px;
  }

  .applications-topbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .applications-refresh-btn {
    width: 100%;
  }

  .trainer-profile-banner {
    align-items: flex-start;
    flex-direction: column;
  }

  .view-profile-btn {
    width: 100%;
  }

  .application-statistics {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .applications-grid {
    grid-template-columns: 1fr;
  }

}


/* =========================================================
   RESPONSIVE - MOBILE
========================================================= */

@media (max-width: 600px) {

  .trainer-applications-page {
    padding: 15px;
  }

  .page-breadcrumb {
    font-size: 10px;
  }

  .page-heading-row {
    align-items: flex-start;
  }

  .page-heading-icon {
    width: 44px;
    height: 44px;

    border-radius: 12px;
  }

  .page-heading-row h1 {
    font-size: 22px;
  }

  .page-heading-row p {
    font-size: 11px;
  }

  .trainer-profile-banner {
    padding: 16px;
  }

  .trainer-profile-main {
    align-items: flex-start;
  }

  .trainer-avatar {
    width: 52px;
    height: 52px;

    border-radius: 14px;

    font-size: 16px;
  }

  .profile-name-row h2 {
    font-size: 15px;
  }

  .verified-badge {
    font-size: 8px;
  }

  .profile-meta {
    align-items: flex-start;
    flex-direction: column;

    gap: 6px;
  }

  .profile-meta span {
    font-size: 10px;
  }

  .application-statistics {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));

    gap: 9px;
  }

  .stat-card {
    padding: 11px;
  }

  .stat-icon {
    width: 31px;
    height: 31px;
  }

  .stat-card span {
    font-size: 9px;
  }

  .stat-card strong {
    font-size: 17px;
  }

  .applications-section-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .applications-section-header h2 {
    font-size: 17px;
  }

  .application-card {
    padding: 15px;
  }

  .application-card-top {
    align-items: flex-start;
    flex-direction: column;
  }

  .application-status {
    align-self: flex-start;
  }

  .application-details {
    grid-template-columns: 1fr;
  }

  .application-detail-content strong {
    font-size: 10px;
  }

  .application-card-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .view-application-btn {
    width: 100%;
  }

  .applications-security-note {
    align-items: flex-start;
  }

}


/* =========================================================
   VERY SMALL MOBILE
========================================================= */

@media (max-width: 380px) {

  .trainer-applications-page {
    padding: 11px;
  }

  .page-heading-row {
    gap: 9px;
  }

  .page-heading-icon {
    width: 40px;
    height: 40px;
  }

  .page-heading-row h1 {
    font-size: 19px;
  }

  .application-statistics {
    grid-template-columns: 1fr;
  }

  .application-status-line {
    display: none;
  }

  .job-title-wrapper h3 {
    font-size: 13px;
  }

}


/* =========================================================
   ACCESSIBILITY
========================================================= */

button:focus-visible {
  outline:
    3px solid
    rgba(149, 100, 221, .28);

  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {

  *,
  *::before,
  *::after {

    animation-duration:
      .01ms !important;

    transition-duration:
      .01ms !important;
  }

}

`;
